import React, { useEffect, useState, useRef, forwardRef } from 'react';
import {
    Dialog, Box, Typography, Button, TextField, Stack, IconButton,
    Alert, CircularProgress, Checkbox, FormControlLabel, Chip, Collapse,
    LinearProgress, Slide, Select, MenuItem, FormControl, InputLabel,
    useTheme, useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BusinessIcon from '@mui/icons-material/Business';
import GroupsIcon from '@mui/icons-material/Groups';
import PrintIcon from '@mui/icons-material/Print';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CelebrationIcon from '@mui/icons-material/Celebration';
import TourIcon from '@mui/icons-material/Tour';
import TuneIcon from '@mui/icons-material/Tune';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { motion, AnimatePresence } from 'framer-motion';
import workplaceService from '../../services/workplace.service';
import employeeService from '../../services/employee.service';
import userService from '../../services/user.service';
import AuthService from '../../services/auth.service';
import { usePreferences } from '../../contexts/PreferencesContext';
import { printWorkplacePoster } from './printPoster';
import { fireConfetti } from './confetti';
import logo from '../../assets/logo_scan.png';

const MotionBox = motion.create(Box);

const SlideUp = forwardRef(function SlideUp(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const slideVariants = {
    enter: (direction) => ({ x: direction > 0 ? 64 : -64, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (direction) => ({ x: direction > 0 ? -64 : 64, opacity: 0 }),
};

const emptyRow = () => ({ name: '', phone: '', position: '', email: '' });

const textFieldSx = {
    '& .MuiOutlinedInput-root': { borderRadius: '12px' },
};

const LANGUAGES = [
    { code: 'SK', flag: '🇸🇰', label: 'Slovenčina' },
    { code: 'CZ', flag: '🇨🇿', label: 'Čeština' },
    { code: 'EN', flag: '🇬🇧', label: 'English' },
];

// Klikateľná karta s výberom (jazyk / téma) — spoločný vzhľad
const ChoiceCard = ({ selected, onClick, children, sx }) => {
    const theme = useTheme();
    return (
        <Box
            onClick={onClick}
            sx={{
                position: 'relative',
                flex: 1,
                minWidth: 0,
                p: 2,
                borderRadius: '16px',
                border: `2px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
                bgcolor: selected ? (theme.palette.mode === 'dark' ? 'rgba(37,99,235,0.12)' : 'rgba(37,99,235,0.05)') : 'transparent',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.18s ease',
                '&:hover': { borderColor: theme.palette.primary.main, transform: 'translateY(-2px)' },
                ...sx,
            }}
        >
            {selected && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <CheckIcon sx={{ fontSize: 14, color: '#fff' }} />
                </Box>
            )}
            {children}
        </Box>
    );
};

const StepHeading = ({ icon, title, hint }) => (
    <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
            {icon}
            <Typography variant="h5" sx={{ fontWeight: 800 }}>{title}</Typography>
        </Stack>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.75 }}>{hint}</Typography>
    </Box>
);

const SetupWizard = ({ open, existingWorkplaces = [], onClose, onFinish, canTour }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { t, language, setLanguage, isDarkMode, setDarkMode } = usePreferences();

    const [activeStep, setActiveStep] = useState(0);
    const [maxVisited, setMaxVisited] = useState(0);
    const [direction, setDirection] = useState(1);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    // Krok 1 — názov firmy (registrácia ani Google signin ho nepýtajú)
    const [companyName, setCompanyName] = useState('');

    // Krok 2 — pracovisko (výber existujúceho alebo vytvorenie ďalšieho)
    const [workplaces, setWorkplaces] = useState([]);
    const [selectedWorkplaceId, setSelectedWorkplaceId] = useState(null);
    const [workplaceName, setWorkplaceName] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const workplace = workplaces.find((w) => w.id === selectedWorkplaceId) || null;

    // Krok 3 — zamestnanci
    const [rows, setRows] = useState([emptyRow(), emptyRow(), emptyRow()]);
    const [sendInvite, setSendInvite] = useState(true);
    const [employeeSummary, setEmployeeSummary] = useState({ created: 0, smsFailed: 0 });
    const [importOpen, setImportOpen] = useState(false);
    const [importText, setImportText] = useState('');
    const [importTextMode, setImportTextMode] = useState(false);
    const [importBusy, setImportBusy] = useState(false);
    const [importInfo, setImportInfo] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const importFileRef = useRef(null);

    // Krok 4 — plagát
    const [posterPrinted, setPosterPrinted] = useState(false);

    useEffect(() => {
        if (!open) return;
        setActiveStep(0);
        setMaxVisited(0);
        setDirection(1);
        setBusy(false);
        setError(null);
        setWorkplaceName('');
        setCreateOpen(false);
        const storedCompany = AuthService.getCurrentUser?.()?.company;
        setCompanyName(storedCompany && storedCompany !== 'Not specified' ? storedCompany : '');
        setWorkplaces(existingWorkplaces);
        setSelectedWorkplaceId(existingWorkplaces[0]?.id ?? null);
        // Čerstvý zoznam pracovísk zo servera (prop môže byť zastaraný)
        workplaceService.getWorkplaces()
            .then((res) => {
                const list = res.data || [];
                setWorkplaces((prev) => {
                    const byId = new Map(list.map((w) => [w.id, w]));
                    prev.forEach((w) => { if (!byId.has(w.id)) byId.set(w.id, w); });
                    return Array.from(byId.values());
                });
                setSelectedWorkplaceId((prev) => prev ?? (list[0]?.id ?? null));
            })
            .catch(() => {});
        setRows([emptyRow(), emptyRow(), emptyRow()]);
        setSendInvite(true);
        setEmployeeSummary({ created: 0, smsFailed: 0 });
        setImportOpen(false);
        setImportText('');
        setImportTextMode(false);
        setImportBusy(false);
        setImportInfo(null);
        setDragOver(false);
        setPosterPrinted(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (open && activeStep === 4) fireConfetti();
    }, [open, activeStep]);

    const steps = [
        { key: 'personalize', label: t('onboardingFlow.wizard.stepPersonalize') },
        { key: 'workplace', label: t('onboardingFlow.wizard.stepWorkplace') },
        { key: 'employees', label: t('onboardingFlow.wizard.stepEmployees') },
        { key: 'poster', label: t('onboardingFlow.wizard.stepPoster') },
        { key: 'done', label: t('onboardingFlow.wizard.stepDone') },
    ];

    const goTo = (step) => {
        setDirection(step > activeStep ? 1 : -1);
        setError(null);
        setActiveStep(step);
        setMaxVisited((prev) => Math.max(prev, step));
    };

    // ---- Personalizácia: ulož názov firmy (potrebný o. i. pre pozvánky) ----
    const handlePersonalizeNext = async () => {
        const name = companyName.trim();
        if (!name) {
            setError(t('onboardingFlow.wizard.personalizeCompanyRequired'));
            return;
        }
        const current = AuthService.getCurrentUser?.();
        if (current && current.company === name) {
            goTo(1);
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await userService.updateCompany(name);
            if (current) {
                localStorage.setItem('user', JSON.stringify({ ...current, company: name }));
            }
            goTo(1);
        } catch (err) {
            setError(err?.response?.data?.message || t('onboardingFlow.wizard.genericError'));
        } finally {
            setBusy(false);
        }
    };

    // ---- Pracovisko: vytvor ďalšie (ostane sa na kroku, nech vidno zoznam) ----
    const handleCreateWorkplace = async () => {
        if (!workplaceName.trim()) {
            setError(t('onboardingFlow.wizard.workplaceRequired'));
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const response = await workplaceService.createWorkplace(workplaceName.trim());
            const created = response.data;
            setWorkplaces((prev) => [...prev, created]);
            setSelectedWorkplaceId(created.id);
            setWorkplaceName('');
            setCreateOpen(false);
        } catch (err) {
            setError(err?.response?.data?.message || t('onboardingFlow.wizard.genericError'));
        } finally {
            setBusy(false);
        }
    };

    const handleWorkplaceNext = async () => {
        // Rozpísané meno nového pracoviska → najprv ho vytvor, potom pokračuj
        if (workplaceName.trim()) {
            setBusy(true);
            setError(null);
            try {
                const response = await workplaceService.createWorkplace(workplaceName.trim());
                const created = response.data;
                setWorkplaces((prev) => [...prev, created]);
                setSelectedWorkplaceId(created.id);
                setWorkplaceName('');
                setCreateOpen(false);
                goTo(2);
            } catch (err) {
                setError(err?.response?.data?.message || t('onboardingFlow.wizard.genericError'));
            } finally {
                setBusy(false);
            }
            return;
        }
        if (!workplace) {
            setError(t('onboardingFlow.wizard.workplaceRequired'));
            return;
        }
        goTo(2);
    };

    // ---- Zamestnanci ----
    const filledRows = rows.filter((r) => r.name.trim() && r.phone.trim());

    const handleRowChange = (index, field, value) => {
        setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    };

    // Spoločné spracovanie odpovede AI parsera (súbor aj text)
    const applyImportResponse = (response) => {
        const parsed = (response.data?.employees || [])
            .filter((e) => (e.name || '').trim())
            .map((e) => ({
                name: (e.name || '').trim(),
                phone: (e.phone || '').trim(),
                position: (e.position || '').trim(),
                email: (e.email || '').trim(),
            }));
        if (parsed.length === 0) {
            setImportInfo({ severity: 'warning', text: t('onboardingFlow.wizard.importNone') });
            return;
        }
        // Rozpoznaní pribudnú do zoznamu; prázdne riadky sa zahodia, ostatné ostávajú
        setRows((prev) => [...prev.filter((r) => r.name.trim() || r.phone.trim()), ...parsed]);
        setImportText('');
        setImportOpen(false);
        setImportInfo({
            severity: 'success',
            text: t('onboardingFlow.wizard.importSuccess').replace('{count}', String(parsed.length)),
        });
    };

    const handleImportFile = async (file) => {
        if (!file) return;
        const name = file.name.toLowerCase();
        if (!name.endsWith('.xlsx') && !name.endsWith('.csv') && !name.endsWith('.txt')) {
            setImportInfo({ severity: 'error', text: t('onboardingFlow.wizard.importBadType') });
            return;
        }
        setImportBusy(true);
        setImportInfo(null);
        try {
            const response = await employeeService.parseImportFile(file);
            applyImportResponse(response);
        } catch (err) {
            setImportInfo({
                severity: 'error',
                text: err?.response?.data?.message || t('onboardingFlow.wizard.genericError'),
            });
        } finally {
            setImportBusy(false);
        }
    };

    const handleImportParse = async () => {
        if (!importText.trim()) {
            setImportInfo({ severity: 'warning', text: t('onboardingFlow.wizard.importEmpty') });
            return;
        }
        setImportBusy(true);
        setImportInfo(null);
        try {
            const response = await employeeService.parseImport(importText);
            applyImportResponse(response);
        } catch (err) {
            setImportInfo({
                severity: 'error',
                text: err?.response?.data?.message || t('onboardingFlow.wizard.genericError'),
            });
        } finally {
            setImportBusy(false);
        }
    };

    const handleEmployeesNext = async () => {
        if (filledRows.length === 0) {
            setError(t('onboardingFlow.wizard.employeesNone'));
            return;
        }
        setBusy(true);
        setError(null);
        const failed = [];
        let created = 0;
        let smsFailed = 0;
        for (const row of filledRows) {
            try {
                const response = await employeeService.createEmployee({
                    name: row.name.trim(),
                    phone_number: row.phone.trim(),
                    position: (row.position || '').trim(),
                    email: (row.email || '').trim(),
                    send_invite: sendInvite,
                });
                created += 1;
                if (response.data?.invite_email === 'failed') smsFailed += 1;
                if (workplace?.id && response.data?.id) {
                    await employeeService.assignWorkplaces(response.data.id, [workplace.id]);
                }
            } catch (err) {
                failed.push(row.name.trim());
            }
        }
        setBusy(false);
        // Akumuluj cez opakované pokusy (po čiastočnom zlyhaní ostávajú len neúspešné riadky)
        setEmployeeSummary((prev) => ({ created: prev.created + created, smsFailed: prev.smsFailed + smsFailed }));
        if (failed.length > 0) {
            setError(t('onboardingFlow.wizard.employeesPartialFail').replace('{names}', failed.join(', ')));
            if (created > 0) {
                setRows((prev) => prev.filter((r) => failed.includes(r.name.trim())));
            }
            return;
        }
        goTo(3);
    };

    // ---- Plagát ----
    const handlePrint = async () => {
        setBusy(true);
        setError(null);
        try {
            await printWorkplacePoster(workplace);
            setPosterPrinted(true);
        } catch (err) {
            setError(err?.message || t('onboardingFlow.wizard.genericError'));
        } finally {
            setBusy(false);
        }
    };

    const renderStep = () => {
        switch (activeStep) {
            case 0:
                return (
                    <Box>
                        <StepHeading
                            icon={<TuneIcon color="primary" sx={{ fontSize: 30 }} />}
                            title={t('onboardingFlow.wizard.personalizeHeading')}
                            hint={t('onboardingFlow.wizard.personalizeHint')}
                        />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                            {t('onboardingFlow.wizard.personalizeCompanyLabel')}
                        </Typography>
                        <TextField
                            fullWidth
                            autoFocus={!isMobile}
                            placeholder={t('onboardingFlow.wizard.personalizeCompanyPlaceholder')}
                            value={companyName}
                            onChange={(e) => setCompanyName(e.target.value)}
                            sx={{ ...textFieldSx, mb: 3.5 }}
                        />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                            {t('onboardingFlow.wizard.languageLabel')}
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                            {LANGUAGES.map((lang) => (
                                <ChoiceCard key={lang.code} selected={language === lang.code} onClick={() => setLanguage(lang.code)}>
                                    <Typography sx={{ fontSize: 30, lineHeight: 1 }}>{lang.flag}</Typography>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>{lang.label}</Typography>
                                </ChoiceCard>
                            ))}
                        </Stack>

                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25, mt: 3.5 }}>
                            {t('onboardingFlow.wizard.themeLabel')}
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                            <ChoiceCard selected={!isDarkMode} onClick={() => setDarkMode(false)}>
                                <Box sx={{ borderRadius: '10px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0', p: 1.25, mb: 1 }}>
                                    <LightModeIcon sx={{ color: '#f59e0b' }} />
                                    <Box sx={{ height: 5, borderRadius: 3, bgcolor: '#e2e8f0', mt: 0.75 }} />
                                    <Box sx={{ height: 5, borderRadius: 3, bgcolor: '#e2e8f0', mt: 0.5, width: '60%' }} />
                                </Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t('onboardingFlow.wizard.themeLight')}</Typography>
                            </ChoiceCard>
                            <ChoiceCard selected={isDarkMode} onClick={() => setDarkMode(true)}>
                                <Box sx={{ borderRadius: '10px', bgcolor: '#0f172a', border: '1px solid #1e293b', p: 1.25, mb: 1 }}>
                                    <DarkModeIcon sx={{ color: '#93c5fd' }} />
                                    <Box sx={{ height: 5, borderRadius: 3, bgcolor: '#334155', mt: 0.75 }} />
                                    <Box sx={{ height: 5, borderRadius: 3, bgcolor: '#334155', mt: 0.5, width: '60%' }} />
                                </Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t('onboardingFlow.wizard.themeDark')}</Typography>
                            </ChoiceCard>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                            {t('onboardingFlow.wizard.personalizeNote')}
                        </Typography>
                    </Box>
                );
            case 1: {
                const hasWorkplaces = workplaces.length > 0;
                const createFieldVisible = !hasWorkplaces || createOpen;
                return (
                    <Box>
                        <StepHeading
                            icon={<BusinessIcon color="primary" sx={{ fontSize: 30 }} />}
                            title={t('onboardingFlow.wizard.workplaceHeading')}
                            hint={hasWorkplaces ? t('onboardingFlow.wizard.workplaceSelectHint') : t('onboardingFlow.wizard.workplaceHint')}
                        />

                        {/* Existujúce pracoviská — len prehľad, výber sa robí pri zamestnancoch a plagáte */}
                        {hasWorkplaces && (
                            <Stack spacing={1} sx={{ maxHeight: 280, overflowY: 'auto', pr: 0.5, mb: 2 }}>
                                {workplaces.map((wp) => (
                                    <Stack
                                        key={wp.id}
                                        direction="row"
                                        spacing={1.5}
                                        alignItems="center"
                                        sx={{
                                            p: 1.75,
                                            borderRadius: '14px',
                                            border: `1px solid ${theme.palette.divider}`,
                                            bgcolor: theme.palette.action.hover,
                                        }}
                                    >
                                        <CheckCircleIcon fontSize="small" sx={{ color: '#16a34a' }} />
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>
                                            {wp.name}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        )}

                        {/* Vytvorenie (ďalšieho) pracoviska */}
                        {hasWorkplaces && (
                            <Button
                                startIcon={<AddIcon />}
                                size="small"
                                onClick={() => setCreateOpen((prev) => !prev)}
                                sx={{ mb: 1, borderRadius: '10px', textTransform: 'none' }}
                            >
                                {t('onboardingFlow.wizard.workplaceAddAnother')}
                            </Button>
                        )}
                        <Collapse in={createFieldVisible}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <TextField
                                    fullWidth
                                    autoFocus={!isMobile && !hasWorkplaces}
                                    size={hasWorkplaces ? 'small' : 'medium'}
                                    label={t('onboardingFlow.wizard.workplaceLabel')}
                                    placeholder={t('onboardingFlow.wizard.workplacePlaceholder')}
                                    value={workplaceName}
                                    onChange={(e) => setWorkplaceName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            if (hasWorkplaces) handleCreateWorkplace(); else handleWorkplaceNext();
                                        }
                                    }}
                                    sx={textFieldSx}
                                />
                                {hasWorkplaces && (
                                    <Button
                                        variant="outlined"
                                        onClick={handleCreateWorkplace}
                                        disabled={busy || !workplaceName.trim()}
                                        sx={{ borderRadius: '12px', fontWeight: 700, flexShrink: 0 }}
                                    >
                                        {t('onboardingFlow.wizard.workplaceCreateBtn')}
                                    </Button>
                                )}
                            </Stack>
                        </Collapse>
                    </Box>
                );
            }
            case 2:
                return (
                    <Box>
                        <StepHeading
                            icon={<GroupsIcon color="primary" sx={{ fontSize: 30 }} />}
                            title={t('onboardingFlow.wizard.employeesHeading')}
                            hint={t('onboardingFlow.wizard.employeesHint').replace('{workplace}', workplace?.name || '')}
                        />

                        {/* Kam sa zamestnanci priradia — jasne označený výber */}
                        <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
                            <InputLabel>{t('onboardingFlow.wizard.employeesAssignLabel')}</InputLabel>
                            <Select
                                value={selectedWorkplaceId ?? ''}
                                label={t('onboardingFlow.wizard.employeesAssignLabel')}
                                onChange={(e) => setSelectedWorkplaceId(e.target.value)}
                                sx={{ borderRadius: '12px' }}
                            >
                                {workplaces.map((wp) => (
                                    <MenuItem key={wp.id} value={wp.id}>{wp.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* AI hromadný import */}
                        <Box
                            sx={{
                                mb: 2.5,
                                borderRadius: '16px',
                                border: `1px solid ${importOpen ? theme.palette.primary.main : theme.palette.divider}`,
                                overflow: 'hidden',
                                transition: 'border-color 0.2s ease',
                            }}
                        >
                            <Box
                                onClick={() => setImportOpen((prev) => !prev)}
                                sx={{
                                    px: 2,
                                    py: 1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.25,
                                    cursor: 'pointer',
                                    bgcolor: theme.palette.action.hover,
                                    '&:hover': { bgcolor: theme.palette.action.selected },
                                }}
                            >
                                <AutoAwesomeIcon sx={{ color: '#F59E0B', fontSize: 20 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        {t('onboardingFlow.wizard.importTitle')}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('onboardingFlow.wizard.importSubtitle')}
                                    </Typography>
                                </Box>
                                <Chip label="AI" size="small" sx={{ bgcolor: 'rgba(245,158,11,0.14)', color: '#D97706', fontWeight: 700 }} />
                            </Box>
                            <Collapse in={importOpen}>
                                <Box sx={{ p: 2, pt: 1.5 }}>
                                    {/* Primárne: upload / drag & drop súboru */}
                                    <input
                                        ref={importFileRef}
                                        type="file"
                                        accept=".xlsx,.csv,.txt"
                                        hidden
                                        onChange={(e) => {
                                            handleImportFile(e.target.files?.[0]);
                                            e.target.value = '';
                                        }}
                                    />
                                    <Box
                                        onClick={() => !importBusy && importFileRef.current?.click()}
                                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                        onDragLeave={() => setDragOver(false)}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            setDragOver(false);
                                            if (!importBusy) handleImportFile(e.dataTransfer.files?.[0]);
                                        }}
                                        sx={{
                                            p: 3.5,
                                            borderRadius: '14px',
                                            border: `2px dashed ${dragOver ? theme.palette.primary.main : theme.palette.divider}`,
                                            bgcolor: dragOver ? 'rgba(37,99,235,0.06)' : 'transparent',
                                            textAlign: 'center',
                                            cursor: importBusy ? 'default' : 'pointer',
                                            transition: 'all 0.18s ease',
                                            '&:hover': { borderColor: theme.palette.primary.main },
                                        }}
                                    >
                                        {importBusy ? (
                                            <>
                                                <CircularProgress size={28} sx={{ mb: 1 }} />
                                                <Typography variant="body2" color="text.secondary">
                                                    {t('onboardingFlow.wizard.importParsing')}
                                                </Typography>
                                            </>
                                        ) : (
                                            <>
                                                <UploadFileIcon sx={{ fontSize: 36, color: theme.palette.primary.main, mb: 0.75 }} />
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                    {t('onboardingFlow.wizard.importDrop')}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {t('onboardingFlow.wizard.importFormats')}
                                                </Typography>
                                            </>
                                        )}
                                    </Box>

                                    {/* Sekundárne: ručné vloženie textu (kontakty z mailu a pod.) */}
                                    <Button
                                        size="small"
                                        onClick={() => setImportTextMode((prev) => !prev)}
                                        disabled={importBusy}
                                        sx={{ mt: 1, borderRadius: '10px', textTransform: 'none', color: 'text.secondary' }}
                                    >
                                        {t('onboardingFlow.wizard.importOrText')}
                                    </Button>
                                    <Collapse in={importTextMode}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={4}
                                            maxRows={9}
                                            placeholder={t('onboardingFlow.wizard.importPlaceholder')}
                                            value={importText}
                                            onChange={(e) => setImportText(e.target.value)}
                                            sx={{ ...textFieldSx, mt: 1 }}
                                        />
                                        <Button
                                            variant="contained"
                                            startIcon={importBusy ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                                            onClick={handleImportParse}
                                            disabled={importBusy}
                                            sx={{
                                                mt: 1.5,
                                                borderRadius: '12px',
                                                fontWeight: 700,
                                                boxShadow: 'none',
                                                background: 'linear-gradient(135deg, #F59E0B 0%, #EA580C 100%)',
                                                '&:hover': { boxShadow: 'none', opacity: 0.92 },
                                            }}
                                        >
                                            {importBusy ? t('onboardingFlow.wizard.importParsing') : t('onboardingFlow.wizard.importParse')}
                                        </Button>
                                    </Collapse>
                                </Box>
                            </Collapse>
                        </Box>
                        {importInfo && (
                            <Alert severity={importInfo.severity} sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setImportInfo(null)}>
                                {importInfo.text}
                            </Alert>
                        )}

                        {/* Zoznam zamestnancov — vlastný scroll, nech sa krok nenaťahuje */}
                        <Box sx={{ maxHeight: { xs: 280, md: 340 }, overflowY: 'auto', pr: 0.5 }}>
                        <Stack spacing={1.5}>
                            {rows.map((row, index) => (
                                <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                    <TextField
                                        size="small"
                                        label={t('onboardingFlow.wizard.employeeName')}
                                        value={row.name}
                                        onChange={(e) => handleRowChange(index, 'name', e.target.value)}
                                        sx={{ ...textFieldSx, flex: 1.2 }}
                                    />
                                    <TextField
                                        size="small"
                                        label={t('onboardingFlow.wizard.employeePhone')}
                                        value={row.phone}
                                        onChange={(e) => handleRowChange(index, 'phone', e.target.value)}
                                        sx={{ ...textFieldSx, flex: 1 }}
                                    />
                                    <TextField
                                        size="small"
                                        label={t('onboardingFlow.wizard.employeePosition')}
                                        value={row.position}
                                        onChange={(e) => handleRowChange(index, 'position', e.target.value)}
                                        sx={{ ...textFieldSx, flex: 1 }}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                                        disabled={rows.length <= 1}
                                        aria-label="remove"
                                        sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            ))}
                        </Stack>
                        </Box>
                        <Button
                            startIcon={<AddIcon />}
                            size="small"
                            onClick={() => setRows((prev) => [...prev, emptyRow()])}
                            sx={{ mt: 1.5, borderRadius: '10px', textTransform: 'none' }}
                        >
                            {t('onboardingFlow.wizard.addRow')}
                        </Button>
                        <Box sx={{ mt: 2, p: 1.5, borderRadius: '12px', bgcolor: theme.palette.action.hover }}>
                            <FormControlLabel
                                control={<Checkbox checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} />}
                                label={<Typography variant="body2">{t('onboardingFlow.wizard.sendInvite')}</Typography>}
                            />
                        </Box>
                    </Box>
                );
            case 3:
                return (
                    <Box>
                        <StepHeading
                            icon={<PrintIcon color="primary" sx={{ fontSize: 30 }} />}
                            title={t('onboardingFlow.wizard.posterHeading')}
                            hint={t('onboardingFlow.wizard.posterHint')}
                        />
                        {workplaces.length > 1 && (
                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                <InputLabel>{t('onboardingFlow.wizard.posterWorkplaceLabel')}</InputLabel>
                                <Select
                                    value={selectedWorkplaceId ?? ''}
                                    label={t('onboardingFlow.wizard.posterWorkplaceLabel')}
                                    onChange={(e) => setSelectedWorkplaceId(e.target.value)}
                                    sx={{ borderRadius: '12px' }}
                                >
                                    {workplaces.map((wp) => (
                                        <MenuItem key={wp.id} value={wp.id}>{wp.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <Box
                            sx={{
                                p: 4,
                                borderRadius: '16px',
                                border: `2px dashed ${theme.palette.divider}`,
                                textAlign: 'center',
                            }}
                        >
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />}
                                onClick={handlePrint}
                                disabled={busy}
                                sx={{ borderRadius: '14px', py: 1.25, px: 3, fontWeight: 700, boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
                            >
                                {t('onboardingFlow.wizard.posterPrint')}
                            </Button>
                            {posterPrinted && (
                                <Typography variant="body2" sx={{ mt: 1.5, color: '#16a34a', fontWeight: 600 }}>
                                    {t('onboardingFlow.wizard.posterPrinted')}
                                </Typography>
                            )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                            {t('onboardingFlow.wizard.posterLater')}
                        </Typography>
                    </Box>
                );
            case 4:
            default:
                return (
                    <Box sx={{ textAlign: 'center' }}>
                        <MotionBox
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'spring', stiffness: 260, damping: 14 }}
                            sx={{
                                width: 88,
                                height: 88,
                                mx: 'auto',
                                mb: 2.5,
                                borderRadius: '50%',
                                bgcolor: 'rgba(22,163,74,0.12)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <CelebrationIcon sx={{ fontSize: 44, color: '#16a34a' }} />
                        </MotionBox>
                        <Typography variant="h4" sx={{ fontWeight: 800 }}>
                            {t('onboardingFlow.wizard.doneHeading')}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 1.25, maxWidth: 440, mx: 'auto' }}>
                            {t('onboardingFlow.wizard.doneText')}
                        </Typography>
                        <Stack spacing={1.25} sx={{ mt: 3, maxWidth: 340, mx: 'auto', textAlign: 'left' }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CheckCircleIcon fontSize="small" sx={{ color: '#16a34a' }} />
                                <Typography variant="body2">
                                    {t('onboardingFlow.wizard.doneSummaryWorkplace')}: <b>{workplace?.name || '—'}</b>
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CheckCircleIcon fontSize="small" sx={{ color: employeeSummary.created > 0 ? '#16a34a' : theme.palette.text.disabled }} />
                                <Typography variant="body2">
                                    {t('onboardingFlow.wizard.doneSummaryEmployees')}: <b>{employeeSummary.created}</b>
                                    {sendInvite && employeeSummary.created > 0 && (
                                        <> · {t('onboardingFlow.wizard.doneSummaryInvites')}: <b>{employeeSummary.created - employeeSummary.smsFailed}</b></>
                                    )}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CheckCircleIcon fontSize="small" sx={{ color: posterPrinted ? '#16a34a' : theme.palette.text.disabled }} />
                                <Typography variant="body2">{t('onboardingFlow.wizard.stepPoster')}</Typography>
                            </Stack>
                        </Stack>
                    </Box>
                );
        }
    };

    const renderActions = () => {
        if (activeStep === 4) {
            return (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: '100%', maxWidth: 460 }}>
                    {canTour && (
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<TourIcon />}
                            onClick={() => onFinish('tour')}
                            sx={{ borderRadius: '14px', py: 1.1, fontWeight: 700 }}
                        >
                            {t('onboardingFlow.wizard.startTour')}
                        </Button>
                    )}
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={() => onFinish()}
                        sx={{ borderRadius: '14px', py: 1.1, fontWeight: 700, boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
                    >
                        {t('onboardingFlow.wizard.finish')}
                    </Button>
                </Stack>
            );
        }

        const nextHandlers = [handlePersonalizeNext, handleWorkplaceNext, handleEmployeesNext, () => goTo(4)];
        const canSkipStep = activeStep === 2 || activeStep === 3;
        return (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                <Button
                    onClick={() => goTo(activeStep - 1)}
                    disabled={activeStep === 0 || busy}
                    sx={{ borderRadius: '12px', color: 'text.secondary', visibility: activeStep === 0 ? 'hidden' : 'visible' }}
                >
                    {t('onboardingFlow.wizard.back')}
                </Button>
                <Box sx={{ flex: 1 }} />
                {canSkipStep && (
                    <Button
                        onClick={() => goTo(activeStep + 1)}
                        disabled={busy}
                        sx={{ borderRadius: '12px', color: 'text.secondary', textTransform: 'none' }}
                    >
                        {t('onboardingFlow.wizard.skipStep')}
                    </Button>
                )}
                <Button
                    variant="contained"
                    onClick={nextHandlers[activeStep]}
                    disabled={busy}
                    startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ borderRadius: '12px', px: 4, py: 1, fontWeight: 700, boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
                >
                    {busy ? t('onboardingFlow.wizard.creating') : t('onboardingFlow.wizard.next')}
                </Button>
            </Stack>
        );
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} fullScreen TransitionComponent={SlideUp}>
            <Box sx={{ display: 'flex', height: '100%', bgcolor: 'background.default' }}>
                {/* Ľavý panel s krokmi — desktop */}
                {!isMobile && (
                    <Box
                        sx={{
                            width: 340,
                            flexShrink: 0,
                            background: 'linear-gradient(165deg, #1E3A8A 0%, #2563EB 62%, #F59E0B 160%)',
                            color: '#fff',
                            p: 4,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 6 }}>
                            <Box
                                component="img"
                                src={logo}
                                alt="Effzone"
                                sx={{ width: 40, height: 40, objectFit: 'contain', borderRadius: '50%', bgcolor: '#fff', p: 0.5 }}
                            />
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>Effzone</Typography>
                        </Stack>

                        <Stack spacing={0.5}>
                            {steps.map((step, idx) => {
                                const isDone = idx < activeStep;
                                const isActive = idx === activeStep;
                                // Už navštívené kroky sú klikateľné — voľný návrat späť
                                const clickable = idx <= maxVisited && idx !== activeStep && !busy;
                                return (
                                    <Stack
                                        key={step.key}
                                        direction="row"
                                        spacing={2}
                                        alignItems="center"
                                        onClick={clickable ? () => goTo(idx) : undefined}
                                        sx={{
                                            py: 1.25,
                                            px: 1,
                                            mx: -1,
                                            borderRadius: '12px',
                                            opacity: isDone || isActive ? 1 : idx <= maxVisited ? 0.75 : 0.45,
                                            transition: 'all 0.2s ease',
                                            cursor: clickable ? 'pointer' : 'default',
                                            ...(clickable && { '&:hover': { bgcolor: 'rgba(255,255,255,0.12)', opacity: 1 } }),
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 34,
                                                height: 34,
                                                borderRadius: '50%',
                                                flexShrink: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: 800,
                                                fontSize: '0.85rem',
                                                bgcolor: isDone ? 'rgba(255,255,255,0.9)' : isActive ? '#fff' : 'transparent',
                                                color: isDone || isActive ? '#1d4ed8' : '#fff',
                                                border: isDone || isActive ? 'none' : '2px solid rgba(255,255,255,0.5)',
                                                transition: 'all 0.3s ease',
                                            }}
                                        >
                                            {isDone ? <CheckIcon sx={{ fontSize: 18 }} /> : idx + 1}
                                        </Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: isActive ? 800 : 600 }}>
                                            {step.label}
                                        </Typography>
                                    </Stack>
                                );
                            })}
                        </Stack>

                        <Box sx={{ mt: 'auto' }}>
                            <Typography variant="body2" sx={{ opacity: 0.85, lineHeight: 1.6 }}>
                                {t('onboardingFlow.wizard.railHint')}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Obsah */}
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    {/* Horná lišta */}
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ px: { xs: 2.5, md: 5 }, py: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                        {isMobile && (
                            <Typography variant="subtitle1" sx={{ fontWeight: 800, flexShrink: 0 }}>
                                {steps[activeStep].label}
                            </Typography>
                        )}
                        <Box sx={{ flex: 1 }}>
                            {isMobile && (
                                <LinearProgress
                                    variant="determinate"
                                    value={((activeStep + 1) / steps.length) * 100}
                                    sx={{ height: 6, borderRadius: 4 }}
                                />
                            )}
                            {!isMobile && (
                                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
                                    {t('onboardingFlow.wizard.title')}
                                </Typography>
                            )}
                        </Box>
                        <IconButton onClick={onClose} disabled={busy} size="small">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Stack>

                    {/* Krok */}
                    <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 2.5, md: 5 }, py: { xs: 3, md: 5 } }}>
                        <Box sx={{ maxWidth: 680, mx: 'auto' }}>
                            <AnimatePresence mode="wait" custom={direction} initial={false}>
                                <MotionBox
                                    key={activeStep}
                                    custom={direction}
                                    variants={slideVariants}
                                    initial="enter"
                                    animate="center"
                                    exit="exit"
                                    transition={{ duration: 0.28, ease: 'easeInOut' }}
                                >
                                    {error && (
                                        <Alert severity="error" sx={{ mb: 2.5, borderRadius: '12px' }} onClose={() => setError(null)}>
                                            {error}
                                        </Alert>
                                    )}
                                    {renderStep()}
                                </MotionBox>
                            </AnimatePresence>
                        </Box>
                    </Box>

                    {/* Akcie */}
                    <Box sx={{ px: { xs: 2.5, md: 5 }, py: 2.5, borderTop: `1px solid ${theme.palette.divider}`, display: 'flex', justifyContent: 'center' }}>
                        <Box sx={{ width: '100%', maxWidth: 680, display: 'flex', justifyContent: activeStep === 4 ? 'center' : 'stretch' }}>
                            {renderActions()}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
};

export default SetupWizard;
